<template>
    <div>
        <div class="row mt-5">
            <div class="col-3 mb-2">
                <form method="post" @submit.prevent="onConnectLedger">
                    <label for="accountIndex">Account Index: (m/44'/501'/x')</label>
                    <input type="text" class="form-control" id="accountIndex" v-model="accountIndex" size="6">
                    <input type="submit" class="btn btn-info mt-1" value="Load">
                </form>
            </div>

            <div class="col-4">
                <label>Address:</label>
                <p class="bold">{{ solAddress }}</p>
            </div>
            <div class="col-3">
                <label>HD Path:</label>
                <p class="bold">{{ hdPath }}</p>
            </div>
            <div class="col-2">
                <button class="btn btn-secondary btn-sm mt-3 mr-1" @click="logout">Logout</button>
                <button class="btn btn-primary btn-sm mt-3" @click="showAddAddressModal = true">Add New Address</button>
                <button class="btn btn-outline-secondary btn-sm mt-1 w-100" @click="showReceive = true" v-if="solAddress !== 'N/A'">📥 Receive</button>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-7">
                <div class="total-usdt-banner" v-if="balances.length">
                    <span class="tub-label">Total Value</span>
                    <span class="tub-value">{{ totalUsdtStr }}</span>
                    <button class="tub-refresh" @click="loadBalances" :disabled="tokensLoading" title="Refresh USDT value">
                        <span v-if="tokensLoading" class="spinner-border spinner-border-sm" role="status"></span>
                        <span v-else>↻</span>
                    </button>
                </div>
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>Asset</th>
                            <th>Balance</th>
                            <th>Value (USDT)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="tokensLoading">
                            <td colspan="4" class="text-center text-muted small py-1">
                                <span class="spinner-border spinner-border-sm mr-1" style="width:.75rem;height:.75rem"></span>
                                {{ pricingProgressMsg }}
                            </td>
                        </tr>
                        <tr v-for="balance in sortedBalances" :key="balance.contractAddress || balance.ticker">
                            <td>{{ balance.ticker }}</td>
                            <td>{{ balance.balance }}</td>
                            <td>{{ balance.usdValue != null ? '$' + balance.usdValue.toFixed(2) : priceOf(balance.ticker, balance.rawBalance !== undefined ? balance.rawBalance : balance.balance) }}</td>
                            <td>
                                <button class="btn btn-success"
                                    @click="openModal(balance.ticker, balance.balance, balance.type, balance.mint, balance.decimals)">
                                    Send {{ balance.ticker }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-sm-5">
                <table class="table table-striped text-center">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Address</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(entry, index) in addresses" :key="index">
                            <td>{{ entry.name }}</td>
                            <td>{{ entry.address }}</td>
                            <td>
                                <button class="btn btn-info btn-sm mx-3" @click="copyAddress(entry.address)">Copy</button>
                                <button class="btn btn-danger btn-sm" @click="deleteAddress(index)">Delete</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="row mt-3">
            <div class="col-12">
                <div class="rpc-panel">
                    <span class="rpc-title">RPC Nodes</span>
                    <span v-for="rpc in rpcList" :key="rpc.url"
                        class="rpc-item"
                        :class="{ 'rpc-active': activeRpc === rpc.url }"
                        @click="switchRpc(rpc.url)">
                        <span class="rpc-dot" :class="rpcStatus[rpc.url]"></span>
                        {{ rpc.label }}
                    </span>
                </div>
            </div>
        </div>

        <!-- ── Transaction Chart ─────────────────────────────────────── -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-toolbar">
                        <span class="chart-panel-title">Transaction History</span>
                        <div class="chart-input-group">
                            <input type="text" class="form-control form-control-sm chart-addr-input"
                                placeholder="Solana address (e.g. 3vfE…)"
                                v-model="chartAddress"
                                @keyup.enter="loadChart" />
                            <button class="btn btn-sol btn-sm" @click="loadChart" :disabled="chartLoading">
                                <span v-if="chartLoading">Loading…</span>
                                <span v-else>Load Chart</span>
                            </button>
                            <button v-if="solAddress !== 'N/A'" class="btn btn-outline-secondary btn-sm"
                                @click="chartAddress = solAddress; loadChart()">
                                My Address
                            </button>
                        </div>
                        <div class="chart-toggles">
                            <button class="btn btn-sm"
                                :class="chartMode==='count' ? 'btn-sol' : 'btn-outline-secondary'"
                                @click="setChartMode('count')">TX Count</button>
                            <button class="btn btn-sm"
                                :class="chartMode==='volume' ? 'btn-sol' : 'btn-outline-secondary'"
                                @click="setChartMode('volume')">SOL Volume</button>
                        </div>
                        <div class="chart-toggles">
                            <button v-for="d in [30,60,90]" :key="d" class="btn btn-sm"
                                :class="chartDays===d ? 'btn-sol' : 'btn-outline-secondary'"
                                @click="chartDays=d; renderChart()">{{ d }}d</button>
                        </div>
                    </div>
                    <div class="chart-summary" v-if="chartStats.total > 0">
                        <span class="chart-stat"><strong>{{ chartStats.total }}</strong> TXs</span>
                        <span class="chart-stat"><span class="stat-in">▲ {{ chartStats.received }} SOL received</span></span>
                        <span class="chart-stat"><span class="stat-out">▼ {{ chartStats.sent }} SOL sent</span></span>
                        <span class="chart-stat text-muted">Last {{ chartDays }} days</span>
                    </div>
                    <div class="chart-wrapper">
                        <canvas ref="txChart"></canvas>
                        <div v-if="!chartLoaded && !chartLoading" class="chart-placeholder">
                            Enter a Solana address above and click <strong>Load Chart</strong>
                        </div>
                        <div v-if="chartLoading" class="chart-placeholder">
                            <div class="spinner-border text-success" role="status"></div>
                            <p class="mt-2 mb-0">Fetching transactions…</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal v-model="showReceipt" title="TX Signature">
            <p class="small">
                <a :href="'https://explorer.solana.com/tx/' + txid" target="_blank">{{ txid }}</a>
                <br /><br />
                <input type="text" class="form-control form-control-sm" v-model="txid" />
            </p>
        </Modal>

        <Modal v-model="showModal" :title="'Send ' + modalTicker">
            <form novalidate>
                <div class="form-group">
                    <label for="inputToAddress">To Address</label>
                    <input id="inputToAddress" type="text" class="form-control" placeholder="To Address"
                        v-model="modalToAddress" />
                </div>
                <div class="form-group">
                    <label for="inputAmount">Amount ({{ modalTicker }})</label>
                    <input id="inputAmount" type="text" class="form-control" placeholder="Amount"
                        v-model="modalAmount" />
                </div>
                <div class="row modal-footer">
                    <div class="col-sm-12">
                        <div class="float-left">
                            <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        </div>
                        <div class="float-right">
                            <button class="btn btn-success" type="button" @click="sendTransaction">Send</button>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>

        <Modal v-model="showAddAddressModal" title="Add New Address">
            <form novalidate @submit.prevent="submitNewAddress">
                <div class="form-group">
                    <label for="inputName">Name</label>
                    <input required id="inputName" type="text" class="form-control" placeholder="Name"
                        v-model="newAddressName" />
                </div>
                <div class="form-group">
                    <label for="inputNewAddress">Address</label>
                    <input required id="inputNewAddress" type="text" class="form-control" placeholder="Address"
                        v-model="newAddress" />
                </div>
                <div class="row modal-footer">
                    <div class="col-sm-12">
                        <div class="float-left">
                            <button class="btn btn-danger" type="button" @click="closeAddAddressModal">Cancel</button>
                        </div>
                        <div class="float-right">
                            <button class="btn btn-success" type="submit">Submit</button>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
        <ReceiveModal v-if="showReceive" :address="solAddress" chain="solana" @close="showReceive = false" />

        <!-- Address Matrix / Tree -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-panel-title mb-2">Address Overview</div>
                    <AddressMatrix
                        :cells="matrixCells"
                        :active-index="accountIndex"
                        accent-color="#9945FF"
                        unit="SOL"
                        :decimals="6"
                        :loading="matrixLoading"
                        root-label="m/44'/501'"
                        hint="Solana keys are hardened-only — only indices you've connected via Ledger appear here."
                        @refresh="loadMatrix(true)"
                        @select="onMatrixSelect" />
                </div>
            </div>
        </div>

        <!-- Transaction History -->
        <WalletTxHistory chain="solana" :address="solAddress" accent-color="#9945FF" />
    </div>
</template>

<script>
import Solana from '@ledgerhq/hw-app-solana';
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import { Chart, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, BarController, LineController } from 'chart.js';
Chart.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, BarController, LineController);
import {
    Connection,
    PublicKey,
    Transaction,
    SystemProgram,
    LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import {
    getAssociatedTokenAddress,
    createTransferInstruction,
    createAssociatedTokenAccountInstruction,
    getAccount,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import numeral from 'numeral';
import BigNumber from 'bignumber.js';
import '@kouts/vue-modal/dist/vue-modal.css';
import axios from 'axios';
import { fetchPrices, formatUsd } from '../services/priceService';
import { saveAccount, getAccounts } from '../services/accountService';
import { getCustomTokens } from '../services/tokenService';
import { loadSolanaTokens } from '../services/tokenLoader';
import { getAddresses, saveAddress, deleteAddress as deleteAddressPb } from '../services/pbService';
import AddressMatrix from './AddressMatrix.vue';
import eventBus from '../services/eventBus';
const ReceiveModal = () => import('./ReceiveModal.vue');

const SOL_RPC_LIST = [
    { label: 'PublicNode',      url: 'https://solana.publicnode.com' },
    { label: 'Solana Official', url: 'https://api.mainnet-beta.solana.com' },
    { label: 'Ankr',            url: 'https://rpc.ankr.com/solana' },
];

let connection = new Connection(SOL_RPC_LIST[0].url, 'confirmed');

// SPL tokens on Solana mainnet
const splTokens = [
    { ticker: 'USDC',  mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6  },
    { ticker: 'USDT',  mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6  },
    { ticker: 'RAY',   mint: '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R', decimals: 6  },
    { ticker: 'BONK',  mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', decimals: 5  },
    { ticker: 'JTO',   mint: 'jtojtomepa8berF47VfC3V7CGYXNMH4sdPne96kHAc',  decimals: 9  },
    { ticker: 'WIF',   mint: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm', decimals: 6  },
];

let transport = null;
let solanaApp = null;

export default {
    name: 'SolanaWallet',
    components: { ReceiveModal, WalletTxHistory: () => import('./WalletTxHistory.vue'), AddressMatrix },
    data() {
        return {
            solAddress: 'N/A',
            solPublicKey: null,
            accountIndex: 0,
            hdPath: 'N/A',
            matrixCells: [],
            matrixLoading: false,
            balances: [],
            tokensLoading: false,
            pricingProgress: null,
            rpcList: SOL_RPC_LIST,
            activeRpc: SOL_RPC_LIST[0].url,
            rpcStatus: {},
            showModal: false,
            showReceipt: false,
            modalTicker: '',
            modalToAddress: '',
            modalAmount: 0,
            modalTxType: '',
            modalMint: '',
            modalDecimals: 0,
            txid: '',
            showAddAddressModal: false,
            newAddressName: '',
            newAddress: '',
            addresses: [],
            // pricing
            prices: {},
            priceTimer: null,
            showReceive: false,
            // chart
            chartAddress: '',
            chartMode: 'count',
            chartDays: 30,
            chartLoading: false,
            chartLoaded: false,
            chartInstance: null,
            chartStats: { total: 0, received: '0', sent: '0' },
        };
    },
    computed: {
        // Native SOL pinned to the top row, everything else sorted by USD value descending.
        sortedBalances() {
            const native = this.balances.filter(b => b.type === 'sol');
            const rest = this.balances.filter(b => b.type !== 'sol')
                .slice()
                .sort((a, b) => this.usdValueNum(b) - this.usdValueNum(a));
            return [...native, ...rest];
        },
        totalUsdt() {
            return this.balances.reduce((sum, b) => sum + Math.max(this.usdValueNum(b), 0), 0);
        },
        totalUsdtStr() {
            return formatUsd(this.totalUsdt) + ' USDT';
        },
        pricingProgressMsg() {
            const p = this.pricingProgress;
            if (!p) return 'Scanning all tokens…';
            if (p.stage === 'binance') return 'Pricing via Binance…';
            if (p.stage === 'coingecko') return `Pricing ${p.current}/${p.total} tokens via CoinGecko…`;
            return 'Scanning all tokens…';
        },
    },
    methods: {
        openModal(ticker, balance, type, mint, decimals) {
            this.showModal = true;
            this.modalTicker = ticker;
            this.modalToAddress = '';
            this.modalAmount = balance;
            this.modalTxType = type;
            this.modalMint = mint || '';
            this.modalDecimals = decimals || 0;
            this.txid = '';
            this.showReceipt = false;
        },

        closeModal() {
            this.showModal = false;
            this.modalTicker = '';
            this.modalToAddress = '';
            this.modalAmount = 0;
            this.modalTxType = '';
        },

        isValidSolanaAddress(address) {
            try {
                new PublicKey(address);
                return true;
            } catch {
                return false;
            }
        },

        async submitNewAddress() {
            if (!this.isValidSolanaAddress(this.newAddress)) {
                alert('Please enter a valid Solana address.');
                return;
            }
            try {
                await saveAddress({
                    label: this.newAddressName,
                    address: this.newAddress,
                    chain: 'solana',
                    notes: '',
                    aml_risk_level: 'unknown',
                });
                await this.fetchAddresses();
                this.newAddressName = '';
                this.newAddress = '';
                this.closeAddAddressModal();
            } catch (error) {
                console.error('Error submitting new address:', error);
                alert('Failed to add new address. Please try again.');
            }
        },

        async fetchAddresses() {
            try {
                const res = await getAddresses();
                this.addresses = res.items
                    .filter(r => r.chain === 'solana' || r.chain === 'all')
                    .map(r => ({ id: r.id, name: r.label, address: r.address }));
            } catch (error) {
                console.error('Error fetching addresses:', error);
            }
        },

        copyAddress(address) {
            navigator.clipboard.writeText(address).then(() => {
                alert('Address copied to clipboard!');
            });
        },

        async deleteAddress(index) {
            const entry = this.addresses[index];
            try {
                if (entry.id) await deleteAddressPb(entry.id);
                this.addresses.splice(index, 1);
            } catch (error) {
                console.error('Error deleting address:', error);
            }
        },

        closeAddAddressModal() {
            this.showAddAddressModal = false;
        },

        logout() {
            localStorage.removeItem('isLoggedIn');
            this.isLoggedIn = false;
        },

        async buildInstructions(fromPubkey, toPubkey) {
            const instructions = [];
            if (this.modalTxType === 'sol') {
                const lamports = Math.round(parseFloat(this.modalAmount) * LAMPORTS_PER_SOL);
                instructions.push(SystemProgram.transfer({ fromPubkey, toPubkey, lamports }));
            } else if (this.modalTxType === 'spl') {
                const mintPubkey = new PublicKey(this.modalMint);
                const fromAta = await getAssociatedTokenAddress(mintPubkey, fromPubkey);
                const toAta   = await getAssociatedTokenAddress(mintPubkey, toPubkey);
                try {
                    await getAccount(connection, toAta);
                } catch {
                    instructions.push(createAssociatedTokenAccountInstruction(
                        fromPubkey, toAta, toPubkey, mintPubkey,
                        TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID,
                    ));
                }
                const amount = new BigNumber(this.modalAmount)
                    .multipliedBy(new BigNumber(10).pow(this.modalDecimals))
                    .toFixed(0);
                instructions.push(createTransferInstruction(
                    fromAta, toAta, fromPubkey, BigInt(amount), [], TOKEN_PROGRAM_ID
                ));
            }
            return instructions;
        },

        async sendTransaction() {
            if (!this.isValidSolanaAddress(this.modalToAddress)) {
                this.notice('error', 'Invalid Address', 'Please enter a valid Solana address.', 5);
                return;
            }

            try {
                const fromPubkey = this.solPublicKey;
                const toPubkey   = new PublicKey(this.modalToAddress);
                const hdPath     = `44'/501'/${this.accountIndex}'`;

                // Build instructions once (ATA check happens here)
                this.notice('info', 'Preparing', 'Building transaction…', 3);
                const instructions = await this.buildInstructions(fromPubkey, toPubkey);

                // Fetch fresh blockhash as late as possible — right before Ledger prompt
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');

                const tx = new Transaction();
                tx.recentBlockhash = blockhash;
                tx.feePayer = fromPubkey;
                instructions.forEach(ix => tx.add(ix));

                this.notice('info', 'Confirm on Ledger', 'Please confirm on your Ledger device.', 60);

                const serializedMessage = tx.serializeMessage();
                const { signature } = await solanaApp.signTransaction(hdPath, serializedMessage);
                tx.addSignature(fromPubkey, Buffer.from(signature));

                this.notice('info', 'Sending', 'Broadcasting transaction…', 10);

                // Send with skipPreflight + maxRetries so network hiccups don't block
                const txid = await connection.sendRawTransaction(tx.serialize(), {
                    skipPreflight: true,
                    maxRetries: 5,
                });

                // Confirm with a generous timeout
                const result = await connection.confirmTransaction(
                    { signature: txid, blockhash, lastValidBlockHeight },
                    'confirmed'
                );

                if (result.value.err) {
                    throw new Error('Transaction failed on-chain: ' + JSON.stringify(result.value.err));
                }

                this.notice('success', 'Sent', 'Transaction confirmed.');
                this.closeModal();
                this.txid = txid;
                this.showReceipt = true;
                setTimeout(() => this.loadBalances(), 10000);
            } catch (error) {
                console.error('Transaction error:', error);
                this.notice('error', 'Transaction Error', error.message || error.toString(), 10);
            }
        },

        notice(type, title, description, duration) {
            this.$notice[type]({
                title,
                description: description || '',
                duration: duration ?? 1,
            });
        },

        async loadBalances() {
            this.balances = [];

            try {
                const lamports = await connection.getBalance(this.solPublicKey);
                const solRaw = lamports / LAMPORTS_PER_SOL;
                this.balances.push({
                    ticker: 'SOL',
                    balance: numeral(solRaw).format('0,0.000000000'),
                    rawBalance: solRaw,
                    type: 'sol',
                });
            } catch (e) {
                console.error('Balance fetch error:', e);
                this.notice('error', 'Balance Error', e.message || e.toString(), 10);
            }

            // Non-blocking SPL token discovery
            this.tokensLoading = true;
            this.pricingProgress = null;
            loadSolanaTokens(this.solPublicKey, connection, p => { this.pricingProgress = p; })
                .then(tokens => {
                    this.balances = this.balances.filter(b => b.type !== 'spl');
                    this.balances.push(...tokens);
                    if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'solana', data: { address: this.solAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
                })
                .catch(e => this.notice('error', 'Token Load Error', e.message, 6))
                .finally(() => { this.tokensLoading = false; this.pricingProgress = null; });

            if (this.$store) {
                this.$store.commit('wallets/SET_WALLET', {
                    chain: 'solana',
                    data: { address: this.solAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex },
                });
            }
        },

        async createTransport() {
            const hidOk = await TransportWebHID.isSupported();
            if (hidOk) return await TransportWebHID.create();
            const usbOk = await TransportWebUSB.isSupported();
            if (usbOk) return await TransportWebUSB.create();
            const isSecure = location.protocol === 'https:' || location.hostname === 'localhost';
            if (!isSecure) {
                throw new Error('Page must be served over HTTPS for Ledger USB access.');
            }
            throw new Error('Ledger USB not supported in this browser. Please use Chrome or Edge.');
        },
        async onConnectLedger(e) {
            e.preventDefault();
            this.notice('info', 'Connecting to Ledger', 'Please wait...', 3);
            try {
                if (transport) {
                    await transport.close().catch(() => {});
                }
                transport = await this.createTransport();
                solanaApp = new Solana(transport);
                const hdPath = `44'/501'/${this.accountIndex}'`;
                const { address } = await solanaApp.getAddress(hdPath);
                this.solPublicKey = new PublicKey(address);
                this.solAddress = this.solPublicKey.toBase58();
                this.hdPath = 'm/' + hdPath;
                this.notice('success', 'Connected', 'Address loaded successfully.');
                await this.loadBalances();
                await this.refreshPrices();
                const accounts = saveAccount({ chain: 'solana', hdIndex: this.accountIndex, address: this.solAddress, label: `Solana #${this.accountIndex}` });
                if (this.$store) this.$store.commit('accounts/SET_ACCOUNTS', accounts);
                await this.loadMatrix();
            } catch (error) {
                console.error(error);
                this.notice('error', 'Ledger Error', error.message || error.toString(), 10);
            }
        },

        // ── Address Matrix / Tree ──────────────────────────────────────────
        // Solana (ed25519/SLIP-0010) only supports hardened derivation, so there's
        // no way to derive sibling addresses from a public key alone — the matrix
        // can only ever show indices the user has already connected via Ledger.
        async loadMatrix(notifyIfEmpty = false) {
            const savedAccounts = getAccounts().filter(a => a.chain === 'solana');
            const cells = savedAccounts
                .map(a => ({ index: Number(a.hdIndex), address: a.address, balance: null, loading: true }))
                .sort((a, b) => a.index - b.index);
            this.matrixCells = cells;
            if (!cells.length) {
                if (notifyIfEmpty) {
                    this.notice('error', 'Address Matrix Empty', 'No connected Solana addresses yet — connect your Ledger to populate it.', 8);
                }
                return;
            }
            this.matrixLoading = true;
            const results = await Promise.allSettled(
                cells.map(async c => ({ index: c.index, lamports: await connection.getBalance(new PublicKey(c.address)) }))
            );
            const byIndex = {};
            for (const r of results) {
                if (r.status === 'fulfilled') byIndex[r.value.index] = r.value.lamports / 1e9;
                else console.error('[solana matrix] balance fetch rejected:', r.reason);
            }
            this.matrixCells = this.matrixCells.map(c => ({ ...c, balance: byIndex[c.index] ?? 0, loading: !(c.index in byIndex) }));
            this.matrixLoading = false;
        },

        onMatrixSelect(idx) {
            const cell = this.matrixCells.find(c => c.index === idx);
            if (!cell) return;
            this.accountIndex = idx;
            this.solAddress = cell.address;
            this.solPublicKey = new PublicKey(cell.address);
            this.hdPath = `m/44'/501'/${idx}'`;
            this.loadBalances();
        },

        async pingRpc(url) {
            this.$set(this.rpcStatus, url, 'checking');
            try {
                const res = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'getSlot', params: [] }),
                });
                this.$set(this.rpcStatus, url, res.ok ? 'ok' : 'error');
            } catch {
                this.$set(this.rpcStatus, url, 'error');
            }
        },

        switchRpc(url) {
            this.activeRpc = url;
            connection = new Connection(url, 'confirmed');
            this.pingRpc(url);
        },

        pingAllRpc() {
            this.rpcList.forEach(r => this.pingRpc(r.url));
        },

        // ── Pricing ────────────────────────────────────────────────────────
        async refreshPrices() {
            const tickers = this.balances.map(b => b.ticker);
            const { prices } = await fetchPrices(tickers);
            this.prices = prices;
            if (this.$store) this.$store.dispatch('prices/fetchPrices', tickers);
        },

        priceOf(ticker, rawBalance) {
            const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[ticker] ?? null;
            if (price === null) return '—';
            return '$' + (raw * price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        // Numeric USD value for sorting — falls back to live price lookup like priceOf(),
        // but returns -1 (sorts last) when no value can be determined at all.
        usdValueNum(b) {
            const rawBalance = b.rawBalance !== undefined ? b.rawBalance : b.balance;
            if (b.usdValue != null) return b.usdValue;
            const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[b.ticker] ?? null;
            return price === null ? -1 : raw * price;
        },

        // ── Chart ─────────────────────────────────────────────────────
        setChartMode(mode) {
            this.chartMode = mode;
            if (this.chartLoaded) this.renderChart();
        },

        async loadChart() {
            const addr = this.chartAddress.trim();
            let pubkey;
            try { pubkey = new PublicKey(addr); } catch {
                this.notice('error', 'Invalid Address', 'Enter a valid Solana base58 address.', 4);
                return;
            }
            this.chartLoading = true;
            this.chartLoaded = false;
            try {
                const sigs = await connection.getSignaturesForAddress(pubkey, { limit: 200 });

                const now = Date.now();
                const msPerDay = 86400000;
                const cutoff = now - this.chartDays * msPerDay;

                const countMap = {}, sentMap = {}, recvMap = {};
                for (let d = 0; d < this.chartDays; d++) {
                    const day = this._dayKey(now - d * msPerDay);
                    countMap[day] = 0; sentMap[day] = 0; recvMap[day] = 0;
                }

                let totalCount = 0, totalSent = 0, totalRecv = 0;
                const inRange = sigs.filter(s => s.blockTime && s.blockTime * 1000 >= cutoff && !s.err);

                inRange.forEach(s => {
                    const day = this._dayKey(s.blockTime * 1000);
                    if (day in countMap) { countMap[day]++; totalCount++; }
                });

                // Fetch parsed transactions for SOL volume (one batched RPC call)
                if (inRange.length > 0) {
                    const txDetails = await connection.getParsedTransactions(
                        inRange.map(s => s.signature),
                        { maxSupportedTransactionVersion: 0 }
                    );
                    txDetails.forEach((tx, i) => {
                        if (!tx || !tx.meta) return;
                        const day = this._dayKey(inRange[i].blockTime * 1000);
                        if (!(day in sentMap)) return;
                        const keys = tx.transaction.message.accountKeys;
                        const idx = keys.findIndex(k => {
                            const pk = k.pubkey ? k.pubkey.toBase58() : String(k);
                            return pk === addr;
                        });
                        if (idx === -1) return;
                        const delta = (tx.meta.postBalances[idx] - tx.meta.preBalances[idx]) / 1e9;
                        if (delta > 0) { recvMap[day] += delta; totalRecv += delta; }
                        else { const v = Math.abs(delta); sentMap[day] += v; totalSent += v; }
                    });
                }

                this.chartStats = {
                    total: totalCount,
                    sent: totalSent.toFixed(4),
                    received: totalRecv.toFixed(4),
                };
                this._chartData = { labels: Object.keys(countMap).sort(), countMap, sentMap, recvMap };
                this.chartLoaded = true;
                this.$nextTick(() => this.renderChart());
            } catch (e) {
                console.error(e);
                this.notice('error', 'Chart Error', e.message || String(e), 8);
            } finally {
                this.chartLoading = false;
            }
        },

        _dayKey(ms) {
            const d = new Date(ms);
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        },

        renderChart() {
            if (!this._chartData) return;
            const { labels, countMap, sentMap, recvMap } = this._chartData;
            if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
            const ctx = this.$refs.txChart;
            if (!ctx) return;

            const shortLabels = labels.map(l => l.slice(5));
            let datasets;
            if (this.chartMode === 'count') {
                datasets = [{
                    label: 'TX Count',
                    data: labels.map(l => countMap[l]),
                    backgroundColor: 'rgba(153,69,255,0.75)',
                    borderColor: '#9945FF',
                    borderWidth: 1,
                    borderRadius: 3,
                }];
            } else {
                datasets = [
                    {
                        label: 'SOL Received',
                        data: labels.map(l => parseFloat(recvMap[l].toFixed(4))),
                        backgroundColor: 'rgba(20,241,149,0.75)',
                        borderColor: '#14F195',
                        borderWidth: 1, borderRadius: 3,
                    },
                    {
                        label: 'SOL Sent',
                        data: labels.map(l => parseFloat(sentMap[l].toFixed(4))),
                        backgroundColor: 'rgba(153,69,255,0.65)',
                        borderColor: '#9945FF',
                        borderWidth: 1, borderRadius: 3,
                    },
                ];
            }

            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: { labels: shortLabels, datasets },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'top' } },
                    scales: {
                        x: {
                            stacked: this.chartMode === 'volume',
                            grid: { display: false },
                            ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 },
                        },
                        y: {
                            stacked: this.chartMode === 'volume',
                            beginAtZero: true,
                            grid: { color: 'rgba(0,0,0,0.05)' },
                        },
                    },
                },
            });
        },

    },

    async mounted() {
        this.fetchAddresses();
        this.pingAllRpc();
        this.priceTimer = setInterval(() => this.refreshPrices(), 60000);
        eventBus.$on('refresh-prices', this.refreshPrices);
        const active = localStorage.getItem('hd_active_solana');
        if (active) {
            const accts = JSON.parse(localStorage.getItem('hd_accounts') || '[]');
            const a = accts.find(x => x.id === active);
            if (a) this.accountIndex = a.hdIndex;
        }
        this.loadMatrix();
    },
    beforeDestroy() {
        clearInterval(this.priceTimer);
        eventBus.$off('refresh-prices', this.refreshPrices);
        if (this.chartInstance) this.chartInstance.destroy();
    },
};
</script>

<style scoped>
/* ── Solana brand ── */
.btn-success {
  background: linear-gradient(90deg, rgba(153,69,255,0.2), rgba(20,241,149,0.2)) !important;
  border: 1px solid rgba(153,69,255,0.5) !important;
  color: #c4b5fd !important;
  font-weight: 600 !important;
}
.btn-success:hover {
  background: linear-gradient(90deg, rgba(153,69,255,0.35), rgba(20,241,149,0.35)) !important;
  color: #fff !important;
}
.btn-primary {
  background: #9945FF !important;
  border-color: #9945FF !important;
  color: #fff !important;
}
.btn-primary:hover { background: #7c2ee0 !important; color: #fff !important; }
.table thead th {
  background: linear-gradient(90deg, rgba(153,69,255,0.2), rgba(20,241,149,0.1)) !important;
  color: #c4b5fd !important;
  border-bottom: 2px solid rgba(153,69,255,0.4) !important;
}

/* ── RPC panel ── */
.rpc-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: var(--surface, #111520);
  border-radius: 10px;
  border: 1px solid var(--border, #242a3a);
  margin-bottom: 4px;
}
.rpc-title {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted, #52597a);
  margin-right: 4px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.rpc-item {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.78rem; font-weight: 500;
  padding: 4px 11px; border-radius: 20px; cursor: pointer;
  border: 1px solid var(--border, #242a3a);
  background: var(--surface-2, #171b28);
  color: var(--text, #c8cfe8);
  transition: all 0.18s;
}
.rpc-item:hover { border-color: #9945FF; background: rgba(153,69,255,0.08); }
.rpc-active { border-color: #9945FF !important; background: rgba(153,69,255,0.15) !important; color: #fff !important; font-weight: 700 !important; }
.rpc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--border-2, #2e3650); display: inline-block; flex-shrink: 0; }
.rpc-dot.ok       { background: #22c55e; }
.rpc-dot.error    { background: #f43f5e; }
.rpc-dot.checking { background: #f59e0b; }

/* ── Chart ── */
.btn-sol {
  background: linear-gradient(90deg, #9945FF, #14F195) !important;
  border: none !important;
  color: #000 !important;
  font-weight: 700 !important;
}
.btn-sol:hover { background: linear-gradient(90deg, #7c2ee0, #0fd47e) !important; color: #000 !important; }
.chart-panel {
  border: 1px solid var(--border, #242a3a);
  border-radius: 12px; padding: 18px;
  background: var(--surface, #111520);
  box-shadow: 0 4px 24px rgba(0,0,0,0.4);
}
.chart-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.chart-panel-title { font-weight: 700; font-size: 0.86rem; color: var(--text, #c8cfe8); white-space: nowrap; margin-right: 6px; }
.chart-input-group { display: flex; gap: 6px; flex: 1; min-width: 200px; }
.chart-addr-input { flex: 1; font-size: 0.82rem; }
.chart-toggles { display: flex; gap: 4px; }
.chart-summary { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px; background: var(--surface-2, #171b28); border-radius: 8px; }
.chart-stat { display: flex; align-items: center; gap: 4px; }
.stat-in  { color: #14F195; font-weight: 700; }
.stat-out { color: #9945FF; font-weight: 700; }
.chart-wrapper { position: relative; height: 280px; }
.chart-placeholder {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: var(--text-muted, #52597a); font-size: 0.88rem;
  background: rgba(11,13,20,0.88); border-radius: 10px;
}
</style>
